// src/components/About.jsx

export default function About() {
  const currentYear = new Date().getFullYear();
  const yearsRunning = currentYear - 2002;
  const avgStudentsPerYear = 72;
  const estimatedTotal = yearsRunning * avgStudentsPerYear;
  const formattedTotal = estimatedTotal.toLocaleString();

  const highlightClass =
    "font-semibold text-maroon transition-colors duration-300 hover:text-yellow-600 hover:underline cursor-default";

  return (
    <section
      id="about"
      aria-label="About BCWD Surathkal Hostel"
      className="relative py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at center bottom, #ffffff 0%, #fffde7 40%, #fff9c4 80%, #fffde7 100%)",
      }}
    >
      {/* Subtle radial glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 rounded-3xl opacity-60 sm:opacity-80"
        style={{
          filter: "blur(100px) sm:blur(120px)",
          background:
            "radial-gradient(circle at center bottom, #fff59d 20%, transparent 80%)",
        }}
      />

      <div className="max-w-6xl mx-auto text-center relative">
        {/* Section Header */}
        <div className="mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-maroon mb-3 sm:mb-4 tracking-wide leading-tight px-2">
            About Our Hostel
          </h2>
          <div className="mx-auto w-16 sm:w-20 md:w-24 lg:w-28 h-1 bg-gradient-to-r from-transparent via-maroon to-transparent rounded-full shadow-md"></div>
        </div>

        {/* Content Card */}
        <article className="bg-white/95 backdrop-blur-sm shadow-xl rounded-xl sm:rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 text-gray-800 leading-relaxed relative z-10 border border-yellow-100 transition-transform transform hover:scale-[1.02] duration-300">
          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-maroon/20 rounded-tl-2xl"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-maroon/20 rounded-br-2xl"></div>

          {/* Paragraph 1 */}
          <p className="mb-6 text-sm sm:text-base md:text-lg lg:text-xl text-left sm:text-center">
            Established in <span className={highlightClass}>2002</span>, the BCWD Surathkal Hostel is a proud initiative by the{" "}
            <span className={highlightClass}>Backward Class Welfare Department</span>, Government of Karnataka. Located in{" "}
            <span className={highlightClass}>Surathkal</span>, our hostel provides a nurturing and disciplined environment for{" "}
            <span className={highlightClass}>125+ students</span> to grow, learn, and succeed.
          </p>

          {/* Stats Highlight Box */}
          <div className="my-6 sm:my-8 md:my-10 p-4 sm:p-5 md:p-6 bg-gradient-to-br from-maroon/5 to-yellow-50 rounded-xl sm:rounded-2xl border-2 border-maroon/10">
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-left sm:text-center">
              Since its inception, approximately{" "}
              <span className="inline-block px-2 sm:px-3 py-1 bg-maroon text-white font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl rounded-lg shadow-md transform hover:scale-105 transition-transform duration-300">
                {formattedTotal}+
              </span>{" "}
              students have become part of our hostel family, with{" "}
              <span className={highlightClass}>70–75 students joining every academic year</span>. We believe in equality, cultural values, and youth empowerment.
            </p>
          </div>

          {/* Paragraph 2 */}
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-left sm:text-center">
            Under the guidance of our warden <span className={highlightClass}>Sri Radhesh</span>, we focus on holistic development with access to modern amenities, nutritious meals, academic support, and cultural enrichment opportunities.
          </p>

          {/* Bottom decorative element */}
          <div className="mt-6 sm:mt-8 flex justify-center">
            <div className="flex gap-2">
              <div className="w-3 h-3 bg-maroon rounded-full animate-pulse"></div>
              <div className="w-3 h-3 bg-yellow-600 rounded-full animate-pulse" style={{ animationDelay: "0.2s" }}></div>
              <div className="w-3 h-3 bg-maroon rounded-full animate-pulse" style={{ animationDelay: "0.4s" }}></div>
            </div>
          </div>
        </article>

        {/* Key Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 mt-8 sm:mt-10 md:mt-12">
          {[
            { value: "2002", label: "Established" },
            { value: "125+", label: "Current Students" },
            { value: `${formattedTotal}+`, label: "Alumni" },
            { value: "70–75", label: "Yearly Intake" },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-yellow-100"
            >
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-maroon mb-1 sm:mb-2">{stat.value}</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
